import { defineStore } from 'pinia';
import { ref } from 'vue';
import { API } from '@vp/common';
import { LoadingState } from '@vp/common/util';
import * as Sentry from '@sentry/vue';
import { useConnectionStore } from '../connection';

/**
 * "Is the current operator VenuePilot staff?" — the signal that decides whether staff-only
 * controls embedded in tenant-facing screens are offered at all.
 *
 * It answers from the internal (VP staff) schema, which authenticates with the superadmin session
 * cookie; the mutations behind those controls enforce the same thing server-side, so this is a UI
 * affordance rather than the security boundary. See `API.Internal.Staff.Api.fetchStaff` for what
 * "staff" currently means and why it coincides with superadmin.
 */
export const useStaffStore = defineStore('@vp/staff', () => {
  const connectionStore = useConnectionStore();
  const internalApiClient = connectionStore.internalGqlApiClient!;

  // Deny by default. Starting from `true` would flash staff-only controls at every tenant
  // operator for as long as the probe takes.
  const isStaff = ref(false);
  const loadingState = ref<LoadingState>(LoadingState.UNKNOWN);

  let inFlight: Promise<void> | null = null;

  const probe = async () => {
    loadingState.value = LoadingState.LOADING;

    const result = await API.Internal.Staff.Api.fetchStaff(internalApiClient);

    if (result.success) {
      isStaff.value = result.data;
      loadingState.value = LoadingState.DONE;
      return;
    }

    isStaff.value = false;

    // The gate's 401 is the expected answer for everyone it refuses — not staff, or a session
    // that lapsed — and either way it is a settled result, not a failure: nothing this store can
    // do will change the answer. Marking it DONE is what stops every order view from re-probing
    // (and eating another 401); it also keeps an expected outcome out of Sentry. Anything else is
    // a real failure: leave it retryable and report it.
    if (API.Internal.isGateRejection(result.errors)) {
      loadingState.value = LoadingState.DONE;
      return;
    }

    loadingState.value = LoadingState.ERROR;
    Sentry.captureException(new Error('Internal staff probe failed'), { extra: { errors: result.errors } });
  };

  // Load once per store instance, and de-dupe the concurrent calls several components make while
  // mounting — the same shape as usePermissionsStore.loadPermissions.
  const ensureLoaded = async (): Promise<void> => {
    if (loadingState.value === LoadingState.DONE) {
      return;
    }

    if (!inFlight) {
      inFlight = probe().finally(() => {
        inFlight = null;
      });
    }

    return inFlight;
  };

  return {
    isStaff,
    loadingState,
    ensureLoaded,
  };
});
